import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import {
  useAllergyMutations,
  type AllergyInput,
} from '@/features/patient-record/hooks/useAllergyMutations';
import type {
  AgeOfOnsetCategory,
  AllergyViewModel,
} from '@/features/patient-record/types';

import { FormField } from '../_shared/FormField';
import { useFocusOnMount } from '../_shared/useFocusOnMount';

const ONSET_CATEGORIES: readonly AgeOfOnsetCategory[] = [
  'unknown',
  'congenital',
  'antenatal',
  'neonatal',
  'pediatric',
  'adult',
];

interface AllergyFormProps {
  readonly guid: string;
  /** When provided, the dialog is in **edit mode** prefilled from the row. */
  readonly record?: AllergyViewModel;
  readonly onClose: () => void;
}

interface AllergyDraft {
  readonly allergen: string;
  readonly notes: string;
  readonly onsetCategory: AgeOfOnsetCategory | '';
  readonly onsetYears: string;
}

const toDraft = (record?: AllergyViewModel): AllergyDraft => ({
  allergen: record?.allergen ?? '',
  notes: record?.notes ?? '',
  onsetCategory: record?.ageOfOnsetCategory ?? '',
  onsetYears:
    record?.ageOfOnsetYears !== undefined && record.ageOfOnsetYears !== null
      ? String(record.ageOfOnsetYears)
      : '',
});

/**
 * Create / edit an allergy in a modal (mirrors the genetic-variant and
 * test-order dialogs). Allergen is the only required field; everything else is
 * optional so clinicians can record what they know without forcing nulls. The
 * draft commits only on the explicit Add / Save button; the dirty guard raises
 * Save / Discard / Keep editing on a dirty dismiss.
 */
export function AllergyForm({
  guid,
  record,
  onClose,
}: AllergyFormProps): ReactElement {
  const { t } = useTranslation();
  const { addAllergy, updateAllergy } = useAllergyMutations(guid);
  const isEdit = record !== undefined;
  const focusRef = useFocusOnMount<HTMLInputElement>();
  const label = t('patient.medicalHistory.allergyFormLabel', { defaultValue: 'Allergy' });

  const pending = addAllergy.isPending || updateAllergy.isPending;

  const toInput = (d: AllergyDraft): AllergyInput => {
    const parsedYears = d.onsetYears.trim().length > 0 ? Number.parseFloat(d.onsetYears) : NaN;
    const trimmedNotes = d.notes.trim();
    return {
      allergen: d.allergen.trim(),
      notes: trimmedNotes.length > 0 ? trimmedNotes : null,
      ageOfOnsetCategory: d.onsetCategory === '' ? null : d.onsetCategory,
      ageOfOnsetYears: Number.isFinite(parsedYears) && parsedYears >= 0 ? parsedYears : null,
    };
  };

  const commit = (d: AllergyDraft): Promise<unknown> =>
    record !== undefined
      ? updateAllergy.mutateAsync({ id: record.id, input: toInput(d) })
      : addAllergy.mutateAsync(toInput(d));

  const form = useEditableDraft<AllergyDraft>({
    initial: toDraft(record),
    label,
    autosave: false,
    canSave: (d) => d.allergen.trim().length > 0,
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  // Explicit Add / Save: commit AND close.
  const submitAndClose = (): void => {
    if (!form.canSave) return;
    void commit(draft).then(onClose).catch(() => undefined);
  };

  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: pending,
    label,
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent
          className="sm:max-w-lg max-h-[calc(100vh-3rem)] p-0 gap-0 flex flex-col"
          {...guard.contentProps}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>
              {isEdit
                ? t('patient.medicalHistory.allergies.edit')
                : t('patient.medicalHistory.allergies.add')}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={form.containerProps.ref}
            className="flex flex-col min-h-0 flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              submitAndClose();
            }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
              <FormField label={t('patient.medicalHistory.allergies.allergenLabel')} required>
                <Input
                  ref={focusRef}
                  value={draft.allergen}
                  onChange={(event) => {
                    form.setDraft((d) => ({ ...d, allergen: event.target.value }));
                  }}
                  placeholder={t('patient.medicalHistory.allergies.allergenPlaceholder')}
                />
              </FormField>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <FormField label={t('patient.medicalHistory.allergies.onsetCategoryLabel')}>
                  <Select
                    value={draft.onsetCategory}
                    onChange={(event) => {
                      form.setDraft((d) => ({
                        ...d,
                        onsetCategory: event.target.value as AgeOfOnsetCategory | '',
                      }));
                    }}
                  >
                    <option value="">
                      {t('patient.medicalHistory.allergies.onsetCategoryUnset')}
                    </option>
                    {ONSET_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {t(`patient.medicalHistory.allergies.onsetCategory.${cat}`)}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label={t('patient.medicalHistory.allergies.onsetYearsLabel')}>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    inputMode="decimal"
                    value={draft.onsetYears}
                    placeholder="—"
                    onChange={(event) => {
                      form.setDraft((d) => ({ ...d, onsetYears: event.target.value }));
                    }}
                    className="tabular-nums"
                  />
                </FormField>
              </div>

              <FormField label={t('patient.medicalHistory.allergies.notesLabel')}>
                <Textarea
                  value={draft.notes}
                  rows={2}
                  onChange={(event) => {
                    form.setDraft((d) => ({ ...d, notes: event.target.value }));
                  }}
                />
              </FormField>
            </div>

            <DialogFooter className="shrink-0 border-t border-border px-6 py-3">
              <Button
                type="button"
                variant="ghost"
                onClick={guard.requestClose}
                disabled={pending}
              >
                {t('ui.dialog.close')}
              </Button>
              <Button
                type="submit"
                disabled={!form.canSave || pending}
                loading={pending}
                loadingText={t('patient.dirtyFooter.saving')}
              >
                {isEdit
                  ? t('patient.medicalHistory.allergies.save')
                  : t('patient.medicalHistory.allergies.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}
